import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    // Capture = riceve anche i toggle di details annidati
    this.addEventListener('toggle', this.#onDetailsToggle, true);

    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('toggle', this.#onDetailsToggle, true);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;
    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);

    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);

        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });

    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  /**
   * Auto-scroll when any nested <details> (accordion/submenu) toggles,
   * keeping its <summary> visible within the appropriate scroll container.
   * @param {Event} event
   */
  #onDetailsToggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;

    // Ignora l'apertura/chiusura del details principale del drawer
    if (details === this.refs.details) return;

    // Extra safety: deve essere davvero dentro questo header-drawer
    if (!this.contains(details)) return;

    const summary = details.querySelector(':scope > summary') || details.querySelector('summary');
    if (!summary) return;

    const container = this.#getBestScrollContainer(details);
    if (!container) return;

    // Aspetta il reflow dopo open/close (cambiano le altezze)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
const target = details.open
  ? (details.querySelector('.details-content') || summary)
  : summary;

target.scrollIntoView({ behavior: 'smooth', block: 'start' });      });
    });
  };

  /**
   * Preferisce scrollare il submenu se stai dentro un pannello submenu,
   * altrimenti scrolla la nav principale.
   * @param {HTMLDetailsElement} details
   * @returns {HTMLElement | null}
   */
  #getBestScrollContainer(details) {
    // Se sei dentro un submenu, quello è spesso lo scroll container più corretto
    const submenu = details.closest('.menu-drawer__submenu');
    if (submenu instanceof HTMLElement) return submenu;

    // Altrimenti la nav principale (con la fix CSS che abbiamo fatto)
    const nav = this.querySelector('.menu-drawer__navigation');
    return nav instanceof HTMLElement ? nav : null;
  }

  /**
   * Scrolla il container per mantenere visibile l'elemento (summary)
   * @param {HTMLElement} container
   * @param {HTMLElement} el
   * @param {number} offset
   */
  #scrollToKeepVisible(container, el, offset = 16) {
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();

    const topLimit = cRect.top + offset;
    const bottomLimit = cRect.bottom - offset;

    // elemento sopra la viewport visibile del container
    if (eRect.top < topLimit) {
      const delta = eRect.top - topLimit;
      container.scrollTo({ top: container.scrollTop + delta, behavior: 'smooth' });
      return;
    }

    // elemento sotto la viewport visibile del container
    if (eRect.bottom > bottomLimit) {
      const delta = eRect.bottom - bottomLimit;
      container.scrollTo({ top: container.scrollTop + delta, behavior: 'smooth' });
    }
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}